/* File: src/pages/GuestPages/CitySelectionPage.js */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvent } from '../../contexts/EventContext';
import { publicApi } from '../../services/api';
import Button from '../../components/common/Button';
import './CitySelectionPage.css';

const CitySelectionPage = () => {
  const navigate = useNavigate();
  const { setSelectedCity } = useEvent();
  const [selectedCity, setLocalSelectedCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);

  // Vérifier que l'utilisateur a bien passé la vérification d'âge
  useEffect(() => {
    const birthYear = localStorage.getItem('birthYear');
    if (!birthYear) {
      navigate('/');
    }
  }, [navigate]);

  // Récupérer les villes avec événements à venir depuis l'API
  useEffect(() => {
    const fetchUpcomingCities = async () => {
      setLoadingCities(true);
      
      try {
        // Récupérer tous les événements à venir
        const response = await publicApi.getUpcomingEvents();
        
        if (response.data.success && response.data.events) {
          // Filtrer et mapper les événements à venir par ville
          const upcomingEvents = response.data.events
            .filter(event => {
              // Vérifier que l'événement est dans le futur
              const eventDate = new Date(event.event_date || event.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return eventDate >= today && event.city;
            })
            .map(event => ({
              name: event.city,
              date: formatDate(event.event_date || event.date),
              time: formatTime(event.event_date || event.date),
              location: event.location,
              eventId: event.event_id || event.id,
              fullDate: new Date(event.event_date || event.date)
            }))
            // Trier par date
            .sort((a, b) => a.fullDate - b.fullDate);
          
          // Dédupliquer les villes (garder seulement le prochain événement par ville)
          const uniqueCities = [];
          const cityNames = new Set();
          
          upcomingEvents.forEach(event => {
            if (!cityNames.has(event.name)) {
              cityNames.add(event.name);
              uniqueCities.push(event);
            }
          });
          
          setCities(uniqueCities);
          
          // Si une seule ville, la sélectionner automatiquement
          if (uniqueCities.length === 1) {
            setLocalSelectedCity(uniqueCities[0].name);
          }
        } else {
          setCities([]);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des villes:', error);
        // En cas d'erreur, essayer de charger depuis localStorage ou utiliser un fallback
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchUpcomingCities();
  }, []);

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Formater l'heure
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleCitySelection = (city) => {
    setLocalSelectedCity(city);
  };

  const handleContinue = async () => {
    if (!selectedCity) {
      return;
    }

    setLoading(true);
    
    try {
      // Sauvegarder la ville sélectionnée dans le contexte et localStorage
      setSelectedCity(selectedCity);
      localStorage.setItem('selectedCity', selectedCity);
      
      // Trouver l'événement correspondant et sauvegarder son ID
      const selectedEvent = cities.find(city => city.name === selectedCity);
      if (selectedEvent) {
        localStorage.setItem('currentEventId', selectedEvent.eventId.toString());
      }
      
      // Rediriger vers la page de l'événement
      navigate('/event');
    } catch (error) {
      console.error('Erreur lors de la sélection de la ville:', error);
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Retour à la vérification d'âge
    navigate('/');
  };

  // Affichage pendant le chargement
  if (loadingCities) {
    return (
      <div className="city-selection-page">
        <div className="city-selection-container">
          <div className="loading-container">
            <img 
              src="/assets/images/guinness-logo.png" 
              alt="Guinness Cameroun" 
              className="city-selection-logo" 
            />
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <p>Chargement des villes disponibles...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage si aucun événement à venir
  if (!loadingCities && cities.length === 0) {
    return (
      <div className="city-selection-page">
        <div className="city-selection-container">
          <div className="back-arrow" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <div className="city-selection-header">
            <img 
              src="/assets/images/guinness-logo.png" 
              alt="Guinness Cameroun" 
              className="city-selection-logo" 
            />
            <h1>PAS D'ÉVÉNEMENT DISPONIBLE</h1>
            <p className="no-event-message">
              Il n'y a pas encore de Guinness Bright Bar programmé pour le moment.
            </p>
            <p className="no-event-submessage">
              Revenez bientôt pour découvrir nos prochains événements exclusifs !
            </p>
          </div>

          <div className="city-selection-actions">
            <Button
              variant="guinness-secondary"
              size="lg"
              fullWidth
              onClick={handleBack}
            >
              RETOUR
            </Button>
          </div>

          <div className="city-selection-footer">
            <p>
              La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
              L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage normal avec les villes disponibles
  return (
    <div className="city-selection-page">
      <div className="city-selection-container">
        <div className="back-arrow" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <div className="city-selection-header">
          <img 
            src="/assets/images/guinness-logo.png" 
            alt="Guinness Cameroun" 
            className="city-selection-logo" 
          />
          <h1>CHOISISSEZ VOTRE VILLE</h1>
          <p>Sélectionnez la ville où vous souhaitez participer à l'événement Guinness Bright Bar</p>
        </div>

        <div className="city-selection-content">
          <div className={`cities-grid ${cities.length > 2 ? 'cities-grid-multiple' : ''}`}>
            {cities.map((city) => (
              <div 
                key={city.name}
                className={`city-card ${selectedCity === city.name ? 'selected' : ''}`}
                onClick={() => handleCitySelection(city.name)}
              >
                <div className="city-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>{city.name.toUpperCase()}</h3>
                <p className="city-date">{city.date}</p>
                <p className="city-location">{city.location.toUpperCase()}</p>
                {selectedCity === city.name && (
                  <div className="city-selected-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="city-selection-actions">
            <Button
              variant="guinness"
              size="lg"
              fullWidth
              onClick={handleContinue}
              disabled={!selectedCity || loading}
            >
              {loading ? 'CHARGEMENT...' : 'CONTINUER'}
            </Button>
          </div>
        </div>

        <div className="city-selection-footer">
          <p>
            La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
            L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CitySelectionPage;